/**
 * This component wraps react-select and allows passing in as the select value just the
 * raw value or an array of raw values instead of an option object { label, value }.
 * It also calls this.props.onChange with the updated raw value. No need to extract
 * the value attribute from the option object.
 *
 * This component also supports the isCreatable prop, which will render a
 * react-select Creatable component. All other props are forwarded on to the
 * underlying react-select component.
 */

import React, { Component } from 'react';
import { any, arrayOf, bool, func, oneOfType, string } from 'prop-types';
import Creatable from 'react-select/creatable';
import Async from 'react-select/async';
import ReactSelect from 'react-select';

import { append } from 'utils/enumerable';
import { isBlank } from 'utils/boolean';
import {
  findOrBuildOption,
  findOrBuildOptionFrom,
  getOptionValue,
  hasOptionShape
} from 'utils/reactSelectUtils';
import {
  SELECT_OPTION_GROUP_SHAPE,
  SELECT_OPTION_SHAPE
} from 'utils/customPropTypes';

export default class Select extends Component {
  static displayName = 'SimpleSelect';

  static propTypes = {
    cacheOptions: bool,
    isAsync: bool,
    isCreatable: bool,
    isMulti: bool,
    loadOptions: func,
    onChange: func,
    onBlur: func,
    onInputChange: func,
    options: arrayOf(
      oneOfType([SELECT_OPTION_GROUP_SHAPE, SELECT_OPTION_SHAPE])
    ),
    value: any, // eslint-disable-line react/forbid-prop-types
    placeholder: string
  };

  static defaultProps = {
    cacheOptions: false,
    isAsync: false,
    isCreatable: false,
    isMulti: false,
    loadOptions: Function.prototype,
    onChange: Function.prototype,
    onBlur: Function.prototype,
    onInputChange: undefined,
    options: [],
    value: undefined,
    placeholder: undefined
  };

  state = { inputValue: '' };

  addInputValue = (val) => {
    const {
      props: { isMulti, options, value },
      getValueAsOption,
      isUnique
    } = this;

    if (val && (!isMulti || isUnique(value, val))) {
      const newOption = findOrBuildOption(options, val);

      this.setState({ inputValue: '' });
      this.handleChange(
        isMulti ? append(getValueAsOption(), newOption) : newOption
      );
    }
  };

  formatCreateLabel = (value) => `Press return to add "${value}"`;

  getValueAsOption = () => {
    const { isMulti, options, value } = this.props;

    // react-select will not clear it's internal state unless [] or '' is received.
    // "undefined" and "null" will not work
    if (isBlank(value)) return isMulti ? [] : '';

    if (Array.isArray(value)) return value.map(findOrBuildOptionFrom(options));

    return hasOptionShape(value) ? value : findOrBuildOption(options, value);
  };

  getComponentClass = () => {
    const { isAsync, isCreatable } = this.props;

    if (isAsync) {
      return Async;
    }
    if (isCreatable) {
      return Creatable;
    }
    return ReactSelect;
  };

  getMergedProps = () => {
    const {
      props: { isAsync, isCreatable, ...props },
      state: { inputValue },
      formatCreateLabel,
      handleInputChange,
      onKeyDown
    } = this;

    const asyncProps = {
      placeholder: 'Enter a value to get options',
      cacheOptions: this.props.cacheOptions,
      ...props,
      onInputChange: isCreatable ? handleInputChange : props.onInputChange
    };

    const creatableProps = {
      formatCreateLabel,
      inputValue,
      onKeyDown,
      ...props,
      onInputChange: isCreatable ? handleInputChange : props.onInputChange
    };

    if (isAsync && isCreatable) return { ...asyncProps, ...creatableProps };
    if (isAsync) return asyncProps;
    if (isCreatable) return creatableProps;
    return props;
  };

  // Must receive an option or array of options { label, value }
  handleChange = (selection) => {
    const { isMulti, onChange } = this.props;

    if (selection === null) {
      onChange(undefined);
    } else if (!Array.isArray(selection)) {
      onChange(getOptionValue(selection));
    } else if (selection.length === 0) {
      if (isMulti) {
        onChange(selection);
      } else {
        onChange(undefined);
      }
    } else {
      onChange(selection.map(getOptionValue));
    }
  };

  handleInputChange = (inputValue) =>
    this.setState({ inputValue }, () => {
      if (typeof this.props.onInputChange === 'function')
        this.props.onInputChange(inputValue);
    });

  handleKeyDown = (event) => {
    const {
      key,
      target: { value: val }
    } = event;

    if (key === 'Enter' || key === 'Tab') this.addInputValue(val);
    if (key === 'Enter') event.preventDefault();
  };

  isDuplicate = (items, value) =>
    items &&
    items.find((item) => (item && item.value === value) || item === value);

  isUnique = (items, value) => !this.isDuplicate(items, value);

  render() {
    const {
      getComponentClass,
      getMergedProps,
      getValueAsOption,
      handleChange
    } = this;

    const Comp = getComponentClass();

    const mergedProps = getMergedProps();

    return (
      <Comp
        {...mergedProps}
        onChange={handleChange}
        value={getValueAsOption()}
      />
    );
  }
}
